/**
 * MobileOnboarding — three-slide intro screen shown to first-time users
 * inside the Capacitor native app. On web this page is gated away.
 *
 * Content (titles + image URLs + button labels) lives in Mongo via the
 * Site Content CMS. Admin edits at /admin/site-content → "Mobile App
 * Onboarding" group. Changes are live-fetched at app open — no rebuild.
 *
 * Route: /welcome
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import api from "../lib/api";
import { isNativeApp } from "../lib/useMobileAppRedirect";

// Default illustrations — used when admin hasn't set a custom image yet.
// SVG illustrations are inlined so they ship with the app and work offline.
// Admin can override with hosted PNG/SVG URLs from /admin/site-content.
// Slide titles have NO fallback text — if the admin leaves a slide's
// headline blank in the CMS, that slide simply shows the image with no
// text underneath, rather than a hardcoded default headline.
const DEFAULTS = [
  {
    titleKey: "mobile_onboarding_slide1_title",
    imageKey: "mobile_onboarding_slide1_image",
    fallbackArt: "surveys",
  },
  {
    titleKey: "mobile_onboarding_slide2_title",
    imageKey: "mobile_onboarding_slide2_image",
    fallbackArt: "notify",
  },
  {
    titleKey: "mobile_onboarding_slide3_title",
    imageKey: "mobile_onboarding_slide3_image",
    fallbackArt: "redeem",
  },
];

// Inline SVG art — kept simple, brand-green (#16A34A), white-on-soft-bg.
// Easy for admin to replace with a hosted illustration later.
function FallbackArt({ kind }) {
  if (kind === "surveys") {
    return (
      <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true">
        <ellipse cx="100" cy="160" rx="70" ry="10" fill="#DCFCE7" />
        <circle cx="100" cy="80" r="42" fill="#16A34A" opacity="0.12" />
        <rect x="115" y="60" width="40" height="64" rx="8" fill="#16A34A" />
        <rect x="120" y="68" width="30" height="4" rx="2" fill="white" />
        <rect x="120" y="78" width="22" height="4" rx="2" fill="white" />
        <rect x="120" y="88" width="26" height="4" rx="2" fill="white" />
        <circle cx="135" cy="115" r="4" fill="white" />
        <path d="M75 60 q-15 10 -10 32 q2 8 -2 16 q-2 4 0 8 l28 0 q4 -4 0 -10 q-4 -10 0 -22 q4 -16 -16 -24z" fill="#0F172A" />
        <circle cx="80" cy="62" r="14" fill="#FCA5A5" />
        <path d="M75 50 q-2 -10 10 -12 q14 -2 14 12 q0 6 -4 8 q-8 -2 -20 -8z" fill="#DC2626" />
      </svg>
    );
  }
  if (kind === "notify") {
    return (
      <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true">
        <ellipse cx="100" cy="170" rx="70" ry="8" fill="#DCFCE7" />
        <rect x="62" y="40" width="76" height="120" rx="10" fill="#16A34A" />
        <rect x="70" y="48" width="60" height="104" rx="4" fill="white" />
        <rect x="78" y="60" width="44" height="20" rx="3" fill="#FECACA" />
        <rect x="78" y="86" width="44" height="32" rx="3" fill="#FECACA" />
        <rect x="84" y="94" width="32" height="3" rx="1.5" fill="#DC2626" />
        <rect x="84" y="102" width="24" height="3" rx="1.5" fill="#DC2626" />
        <rect x="84" y="110" width="28" height="3" rx="1.5" fill="#DC2626" />
        <circle cx="100" cy="156" r="4" fill="#16A34A" />
        <circle cx="146" cy="50" r="18" fill="#FECACA" />
        <path d="M140 44 a6 6 0 0 1 12 0 v6 q4 2 4 6 h-20 q0 -4 4 -6 z" fill="#DC2626" />
        <circle cx="146" cy="62" r="2" fill="#DC2626" />
      </svg>
    );
  }
  // redeem
  return (
    <svg viewBox="0 0 200 200" className="w-full h-full" aria-hidden="true">
      <ellipse cx="100" cy="170" rx="70" ry="8" fill="#DCFCE7" />
      <rect x="62" y="40" width="76" height="120" rx="10" fill="#16A34A" />
      <rect x="70" y="48" width="60" height="104" rx="4" fill="white" />
      <circle cx="100" cy="100" r="28" fill="none" stroke="#FECACA" strokeWidth="6" />
      <path d="M100 72 a28 28 0 0 1 24 14" fill="none" stroke="#DC2626" strokeWidth="6" strokeLinecap="round" />
      <text x="100" y="108" textAnchor="middle" fontFamily="monospace" fontSize="20" fontWeight="800" fill="#DC2626">2340</text>
      <rect x="92" y="58" width="16" height="10" rx="2" fill="#DC2626" />
      <path d="M86 64 q14 -10 28 0" fill="#DC2626" />
    </svg>
  );
}

export default function MobileOnboarding() {
  const nav = useNavigate();
  // Seed from localStorage so first paint already uses last-known content —
  // eliminates the flash of old/default content on refresh.
  const [content, setContent] = useState(() => {
    try {
      const cached = localStorage.getItem("oodel_site_content_cache");
      return cached ? JSON.parse(cached) : {};
    } catch { return {}; }
  });
  // contentResolved → we KNOW whether the admin has set an image for each slide
  // (either from a successful API call, or from a populated localStorage cache).
  // Until then, we render a neutral skeleton — NOT the SVG fallback — so the user
  // never sees the wrong picture flash before the right one loads.
  const [contentResolved, setContentResolved] = useState(() => {
    try {
      const cached = localStorage.getItem("oodel_site_content_cache");
      return !!cached;
    } catch { return false; }
  });
  // Tracks which slide indices had their custom <img> fail to load (broken
  // URL, host blocking hotlinking, offline, etc.) so we can gracefully swap
  // in the built-in SVG illustration instead of leaving a blank box.
  const [imageFailed, setImageFailed] = useState({});
  // Tracks which custom images have FINISHED downloading+decoding — while a
  // custom image is still loading (slow mobile connection), we keep a
  // pulsing skeleton visible underneath so the screen is never blank, then
  // fade the real image in once ready.
  const [imageLoaded, setImageLoaded] = useState({});
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: "start" });
  const [selected, setSelected] = useState(0);

  useEffect(() => {
    // On the web preview, don't show this page — bounce back to landing.
    // (Defence in depth: App.js gates this route, but if someone deep-links here from a browser
    // we still hide it.)
    if (!isNativeApp() && !window.location.search.includes("preview=1")) {
      nav("/", { replace: true });
      return;
    }
    api.get("/site-content").then((r) => {
      const data = r.data || {};
      setContent(data);
      setContentResolved(true);
      try { localStorage.setItem("oodel_site_content_cache", JSON.stringify(data)); } catch {}
      // Preload each slide image into the browser's HTTP cache so the next paint
      // doesn't show a flicker between <img> mount and image bytes arriving.
      DEFAULTS.forEach((s) => {
        const url = data[s.imageKey];
        if (url) { const im = new Image(); im.src = url; }
      });
    }).catch(() => {
      // Even on network error we must stop showing the skeleton — fall back to
      // the built-in SVG illustrations so the screen isn't blank forever.
      setContentResolved(true);
    });
  }, [nav]);

  // Track which slide is in view to highlight the right dot.
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelected(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi]);

  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);
  const ctaPrimary = content.mobile_onboarding_cta_primary || "Sign up";
  const ctaSecondary = content.mobile_onboarding_cta_secondary || "Sign in";

  // Per-slide enable toggles — admin can disable any slide individually from
  // Site Content. Default for every slide is enabled. We filter DEFAULTS down
  // to ONLY the active slides so the carousel doesn't render dead frames.
  const isSlideEnabled = (i) => {
    const v = content[`mobile_onboarding_slide${i + 1}_enabled`];
    if (v === undefined || v === null || v === "") return true;
    return String(v).toLowerCase() !== "false";
  };
  const SLIDES = DEFAULTS.filter((_, i) => isSlideEnabled(i));
  // Edge case: admin disabled all 3 — show slide 1 anyway as the fallback so
  // the screen is never blank.
  const ACTIVE_SLIDES = SLIDES.length > 0 ? SLIDES : [DEFAULTS[0]];

  return (
    <div
      className="fixed inset-0 bg-white flex flex-col"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
      data-testid="mobile-onboarding"
    >
      {/* Top spacer — Skip button removed per product decision; users must
          go through the slides or use the CTA buttons below. */}
      <div className="p-4" />

      {/* Slides */}
      <div className="flex-1 overflow-hidden" ref={emblaRef}>
        <div className="flex h-full">
          {ACTIVE_SLIDES.map((slide, i) => {
            const title = content[slide.titleKey];
            const imageUrl = content[slide.imageKey];
            return (
              <div
                key={i}
                className="flex-[0_0_100%] min-w-0 flex flex-col items-center justify-center px-8 text-center"
                data-testid={`onboarding-slide-${i + 1}`}
              >
                <div className="w-64 h-64 mb-12 flex items-center justify-center">
                  {!contentResolved ? (
                    // Neutral skeleton — NEVER render the wrong picture before we know
                    // what the admin has actually set. This kills the flash bug for
                    // first-time native opens when localStorage is empty.
                    <div className="w-48 h-48 rounded-full bg-neutral-100 animate-pulse" aria-hidden="true" />
                  ) : imageUrl && !imageFailed[i] ? (
                    <div className="relative w-full h-full flex items-center justify-center">
                      {!imageLoaded[i] && <div className="absolute w-48 h-48 rounded-full bg-neutral-100 animate-pulse" aria-hidden="true" />}
                      <img
                        src={imageUrl}
                        alt=""
                        className={`relative max-w-full max-h-full object-contain transition-opacity duration-300 ${imageLoaded[i] ? "opacity-100" : "opacity-0"}`}
                        onLoad={() => setImageLoaded((prev) => ({ ...prev, [i]: true }))}
                        onError={() => setImageFailed((prev) => ({ ...prev, [i]: true }))}
                      />
                    </div>
                  ) : (
                    <FallbackArt kind={slide.fallbackArt} />
                  )}
                </div>
                {contentResolved && title && (
                  <h2
                    className="text-2xl font-black tracking-tight text-neutral-900 leading-snug max-w-sm"
                    data-testid={`onboarding-title-${i + 1}`}
                  >
                    {title}
                  </h2>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Dots — hidden when only one slide is active (no navigation needed) */}
      {ACTIVE_SLIDES.length > 1 && (
        <div className="flex justify-center gap-2 py-6">
          {ACTIVE_SLIDES.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-2 rounded-full transition-all ${
                selected === i ? "w-8 bg-[#16A34A]" : "w-2 bg-neutral-300"
              }`}
              data-testid={`onboarding-dot-${i + 1}`}
            />
          ))}
        </div>
      )}

      {/* CTAs */}
      <div className="px-6 pb-8 space-y-3">
        <Link
          to="/register"
          replace
          className="block w-full text-center rounded-full bg-[#16A34A] hover:bg-[#15803D] active:bg-[#166534] text-white font-bold py-4 text-base transition-colors"
          data-testid="onboarding-signup"
        >
          {ctaPrimary}
        </Link>
        <Link
          to="/login"
          replace
          className="block w-full text-center rounded-full border-2 border-[#16A34A] text-[#16A34A] font-bold py-4 text-base hover:bg-[#16A34A]/5 transition-colors"
          data-testid="onboarding-signin"
        >
          {ctaSecondary}
        </Link>
      </div>
    </div>
  );
}
